/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { LaravelModelsEnum } from '../../../models/model.model';
import { MediaLibraryService } from '../media-library.service';
import { ApiQuery } from '@app/core/http/api-query';
import { HttpResponse, Logger } from '@app/core';
import { HttpClient } from '@angular/common/http';
import { Media } from '@app/shared/components/media-library/media.model';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { FileUpload } from 'primeng/fileupload';
import { MediaLibraryAcceptTypes } from '@app/shared/components/media-library/media-library-accept-types.enum';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer } from '@angular/platform-browser';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import * as FileSaver from 'file-saver';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { WebsitesService } from '@app/settings/websites/websites.service';
import { cloneDeep, uniq } from 'lodash';

const log = new Logger('MediaLibraryComponent');

@Component({
  selector: 'app-media-library',
  templateUrl: './media-library.component.html',
  styleUrls: ['./media-library.component.scss'],
})
export class MediaLibraryComponent implements OnInit {
  modelId: number;
  modelType: LaravelModelsEnum;
  acceptType: MediaLibraryAcceptTypes = MediaLibraryAcceptTypes.IMAGES;
  collectionName: string;
  selectedFiles: Array<any> = [];
  fileForPreview: any = null;
  searchForm: FormGroup;

  mediaData: PaginatedResponse = new PaginatedResponse();

  localesFeature = false;

  companyWebsiteLocales: Array<any>;

  constructor(
    private mediaService: MediaLibraryService,
    private cd: ChangeDetectorRef,
    private http: HttpClient,
    public modal: NgbActiveModal,
    private sanitizer: DomSanitizer,
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    private toastr: ToastrService,
    private websitesService: WebsitesService
  ) {}

  ngOnInit() {
    this.searchForm = this.formBuilder.group({
      keyword: [null],
    });
    this.loadData();
  }

  /**
   * Качва файл на сървъра
   * @param event Event
   * @param fileUploader fileUploader
   */
  upload(event: any, fileUploader: FileUpload): void {
    const promises: Array<any> = [];

    event.files.forEach((file: File) => {
      promises.push(this.readFile(file));
    });

    Promise.all(promises)
      .then((files: Array<{}>) => {
        const body = {
          model_id: this.modelId,
          model_type: this.modelType,
          files: files,
        };

        if (this.collectionName) {
          body['collection_name'] = this.collectionName;
        }
        this.http.post('v1/media', body).subscribe((response: HttpResponse) => {
          response.data.map((media: any) => {
            if (this.localesFeature) {
              media['meta_front'] = {};
              media['meta_front']['locales'] = cloneDeep(this.companyWebsiteLocales);
              media['meta_front']['locales'].forEach((locale: any) => {
                if (!locale.state) {
                  locale['state'] = 1;
                }
              });
            }
            this.mediaData.data.push(new Media().deserialize(media));
          });
        });
      })
      .finally(() => {
        fileUploader.clear();
      });
  }

  /**
   *  Изтрива файл
   * @param media Media
   */
  delete(media: Media): void {
    this.mediaService.destroy(media.id).subscribe(() => {
      this.mediaData.data.splice(this.mediaData.data.indexOf(media), 1);
      this.loadData();
      this.fileForPreview = null;
      this.selectedFiles.splice(this.selectedFiles.indexOf(media.id), 1);
    });
  }

  /**
   * Изтрива множество файлове
   */
  deleteMultiple(): void {
    this.mediaService.massDestroy(this.selectedFiles).subscribe(() => {
      this.selectedFiles = [];
      this.loadData();
      this.fileForPreview = null;
    });
  }

  /**
   * Сваля файлове в .zip
   */
  downloadMultiple(): void {
    this.mediaService.massDownload(this.selectedFiles).subscribe((data) => {
      const blob = new Blob([data], {
        type: 'application/zip',
      });
      FileSaver.saveAs(blob, 'media' + moment().format('DD-MM-YYYY-HH-MM-ss') + '.zip');
    });
  }

  test(a: any) {
    log.debug(a);
  }

  /**
   *  Стандартен paginate handle
   * @param event Event
   */
  paginate(event: any = null): void {
    let page = 1;
    if (event && event.first > 0) {
      page = event.first / event.rows + 1;
    }

    this.loadData(page);
  }

  /**
   *  Отваря preview-то.
   *  Записва файла, който ще се преглежда в променлива.
   * @param file File
   */
  filePreview(file: any): void {
    this.fileForPreview = file;
  }

  /**
   * Затваря preview-то.
   * Занулява променливата за преглеждане на файл.
   */
  closeFilePreview(): void {
    this.fileForPreview = null;
  }

  /**
   * Дали е селектнат checkbox-а на файла
   * @param fileId FileID
   */
  isFileSelected(fileId: number): boolean {
    return !!this.selectedFiles.find((file: any) => file === fileId);
  }

  // @todo: само се тества все още
  // идеята е да отваря документи в iframe от google docs или microsoft
  safeUrl(url: string) {
    log.debug(this.sanitizer.bypassSecurityTrustResourceUrl(url));
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  /**
   * Пали при промяна на checkbox-a на файла
   * @param media Media
   */
  fileCheckboxChange(media: any): void {
    log.debug('change', media);
    if (this.findFileIndex(media.id) > -1) {
      log.debug('splice');
      this.selectedFiles.splice(this.findFileIndex(media.id), 1);
    } else {
      log.debug('push');
      this.selectedFiles.push(media.id);
    }
    log.debug(this.selectedFiles);
  }

  updateMedia(fileForPreview: any) {
    log.debug(fileForPreview);

    const formattedPost = new Media().deserialize({
      id: fileForPreview.id,
      name: fileForPreview.name,
    });

    if (this.localesFeature) {
      formattedPost['visibility'] = fileForPreview.meta_front.locales.map((locale: any) => {
        return {
          id: locale.id,
          state: locale.state,
        };
      });
    }
    this.mediaService.update(formattedPost).subscribe(
      (res: any) => {
        log.debug(res);
        this.translate.get('MEDIA_LIBRARY.SUCCESS_UPDATE_FILE_MSG').subscribe((trans: string) => {
          this.toastr.success(trans);
        });
      },
      () => {
        this.translate.get('MEDIA_LIBRARY.FAIL_UPDATE_FILE_MSG').subscribe((trans: string) => {
          this.toastr.error(trans);
        });
      }
    );
  }

  selectAllFilesOnPage() {
    const allIds = this.mediaData.data.map((item) => {
      return item.id;
    });
    this.selectedFiles = uniq([...this.selectedFiles, ...allIds]);
  }

  deSelectAllFiles() {
    this.selectedFiles = [];
  }

  /**
   * Връща индекса на File-a. Използва се като indexOf но работи
   * Връща -1 ако не намери такъв File
   * @param fileId FileId
   */
  private findFileIndex(fileId: any): number {
    return this.selectedFiles.indexOf(
      this.selectedFiles.find((n: any) => {
        return n === fileId;
      })
    );
  }

  private readFile(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        this.cd.markForCheck();
        resolve({ base64: reader.result, file_name: file.name });
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  }

  /**
   *  Взима файлове от сървъра.
   * @param page Страница
   */
  private loadData(page: number = 1) {
    const query = new ApiQuery()
      .setPage(page)
      .addInclude('user')
      .addFilters({
        model_id: this.modelId,
        model_type: this.modelType,
      })
      .setLimit(9999999999);

    if (this.collectionName) {
      query.addFilter('collection_name', this.collectionName);
    }

    this.mediaService.index(query).subscribe((data: PaginatedResponse) => {
      log.debug('geeeeeeeeeeeeeeet');
      this.mediaData = data;

      if (this.localesFeature) {
        this.loadWebSitesLocales().then((result) => {
          this.mediaData.data.forEach((media: any) => {
            media['meta_front'] = {};
            media['meta_front']['locales'] = cloneDeep(this.companyWebsiteLocales);

            media['meta_front']['locales'].forEach((locale: any) => {
              if (!locale.state) {
                locale['state'] = 1;
              }

              if (media.properties && media.properties.visibility) {
                media.properties.visibility.forEach((visibility: any) => {
                  if (visibility.id === locale.id) {
                    locale['state'] = visibility.state;
                  }
                });
              }
            });
          });
        });
      }
    });
  }

  private loadWebSitesLocales() {
    return new Promise((resolve, reject) => {
      if (!this.companyWebsiteLocales) {
        this.websitesService.getLocales().subscribe(
          (websiteLocales: []) => {
            this.companyWebsiteLocales = websiteLocales;
            resolve(websiteLocales);
          },
          () => {
            reject(false);
          }
        );
      } else {
        resolve(this.companyWebsiteLocales);
      }
    });
  }

  // Computed property to filter media items based on search input, replacement for ng2-search-filter
  get filteredMedia() {
    // Type assertion: we assert the 'keyword' control to be a FormControl
    const keywordControl = this.searchForm.get('keyword') as FormControl;
    const keyword = keywordControl.value?.toLowerCase(); // Ensure lowercase for filtering
    if (!keyword) {
      return this.mediaData.data; // If no keyword is provided, return all media items
    }
    return this.mediaData.data.filter(
      (media) => media.name.toLowerCase().includes(keyword) // Filter media by name
    );
  }
}
